import React, { useState } from 'react';
import _get from 'lodash/get';
import { client } from 'cccisd-apollo';
import IconCheck from 'cccisd-icons/checkmark4';
import IconQuestion from 'cccisd-icons/question4';
import Loader from 'cccisd-loader';
import Modal from 'cccisd-modal';
import Table from 'cccisd-table';
import Tooltip from 'cccisd-tooltip';
import { getSessionChecklistParentDeploymentId } from '../../../helpers/helpers.js';
import sessionChecklistQuery from './sessionChecklist.graphql';

export const TFH_SESSION_CHECKLIST_DEPLOYMENT_HANDLE = 'tfh_session_checklist';
const Boilerplate = window.cccisd.boilerplate;

const SessionChecklist = ({ family, familyName }) => {
    const [isLoading, setIsLoading] = useState(true);
    const [startNewLink, setStartNewLink] = useState('');
    const [data, setData] = useState(null);

    const familyPawnId = family?.descendantRoles?.metricspawn?.pawn?.pawnId || -1;
    const familyPawnHash = family?.descendantRoles?.metricspawn?.pawn?.pawnHash || '';

    async function getData() {
        setIsLoading(true);
        const parentId = await getSessionChecklistParentDeploymentId();
        if (!parentId) {
            console.error(
                'Could not get parent deployment ID for deployment with handle ' +
                    TFH_SESSION_CHECKLIST_DEPLOYMENT_HANDLE
            );
        }

        const resp = await client.query({
            query: sessionChecklistQuery,
            variables: {
                pawnId: familyPawnId,
                pawnHash: familyPawnHash,
                parentDeploymentId: parentId,
            },
            fetchPolicy: 'network-only',
        });

        const respondentHash = _get(resp, 'data.roles.metricspawn.pawn.respondentHash', null);
        const parentDeploymentHash = _get(resp, 'data.deployments.deployment.hash', null);
        if (!respondentHash) {
            console.error('Could not find respondent hash for metricspawn.');
        } else if (!parentDeploymentHash) {
            console.error(
                'Could not find deployment hash for deployment with handle ' + TFH_SESSION_CHECKLIST_DEPLOYMENT_HANDLE
            );
        } else {
            setStartNewLink(Boilerplate.url(`/session-checklist/${respondentHash}`));
        }

        const aps = _get(resp, 'data.roles.metricspawn.assignmentProgressList', []) || [];
        setData(
            aps.map(ap => {
                const childDeploymentHash = _get(ap, 'deployment.hash');
                return {
                    apId: ap.assignmentProgressId,
                    sessionNumber: ap.devTags.sessionChecklistSessionNumber,
                    sessionDate: ap.devTags.sessionChecklistSessionDate,
                    startedDate: ap.startedDate,
                    completedDate: ap.completedDate,
                    editLink: Boilerplate.url(`/session-checklist/${respondentHash}/${ap.deployment.hash}`),
                    reviewLink:
                        childDeploymentHash &&
                        Boilerplate.url(`/print/session-checklist/${respondentHash}/${childDeploymentHash}`),
                };
            })
        );
        setIsLoading(false);
    }

    const trigger = (
        <button type="button" className="btn btn-sm btn-secondary">
            <IconCheck spaceRight />
            Session Checklist
        </button>
    );

    return (
        <Modal
            size="large"
            title={`Session Checklists for ${familyName}`}
            trigger={trigger}
            beforeShow={getData}
            render={({ closeModal }) => {
                if (isLoading || !Array.isArray(data)) {
                    return <Loader loading type="inline" />;
                }

                const startNewButton = (
                    <a
                        className={'btn btn-lg btn-primary ' + (startNewLink ? '' : 'disabled')}
                        type="link"
                        href={startNewLink || '#'}
                        aria-disabled={startNewLink ? 'false' : 'true'}
                        target={startNewLink ? '_blank' : '_self'}
                        rel="noopener noreferrer"
                    >
                        Start New Session
                    </a>
                );

                const closeButton = (
                    <div className="mt-3">
                        <button className="btn btn-secondary" type="button" onClick={() => closeModal()}>
                            Close
                        </button>
                    </div>
                );

                const tooltip = (
                    <Tooltip
                        placement="bottom"
                        title="The Session Checklist of important program elements should be completed
                                                during each family session."
                    >
                        <a className="link-offset-2 link-underline link-underline-opacity-50" href="#">
                            <small>
                                <u>
                                    What is the Session Checklist <IconQuestion />
                                </u>
                            </small>
                        </a>
                    </Tooltip>
                );

                if (data.length === 0) {
                    return (
                        <>
                            <div className="alert alert-primary">
                                <p>
                                    You have not completed the Session Checklist yet for <b>{familyName}</b>. Click to
                                    begin a new session:
                                </p>
                                <div className="text-center my-4">{startNewButton}</div>
                                <p className="mb-0 mt-5">{tooltip}</p>
                            </div>
                            {closeButton}
                        </>
                    );
                }

                return (
                    <>
                        <div className="text-center mt-2">
                            {startNewButton}
                            <p className="mt-2 mb-5">{tooltip}</p>
                        </div>
                        <Table
                            name={familyPawnId + 'sessionChecklists'}
                            data={data}
                            rowKey="apId"
                            isAscentOrder={false}
                            showPerPageOptions={false}
                            saveState={false}
                            columns={[
                                {
                                    name: 'sessionNumber',
                                    label: 'Session Number',
                                    sort: true,
                                    sortSettings: {
                                        custom(row) {
                                            // sort missing values to the bottom of the table
                                            return row.sessionNumber || -1;
                                        },
                                    },
                                    filter: true,
                                    class: 'text-center',
                                },
                                {
                                    name: 'sessionDate',
                                    label: 'Session Date',
                                    sort: true,
                                    filter: true,
                                    filterSettings: { type: 'date' },
                                },
                                {
                                    name: 'startedDate',
                                    label: 'Started',
                                    sort: true,
                                    filter: true,
                                    filterSettings: { type: 'date' },
                                },
                                {
                                    name: 'completedDate',
                                    label: 'Completed',
                                    sort: true,
                                    filter: true,
                                    filterSettings: { type: 'date' },
                                },
                                {
                                    name: 'editLink',
                                    label: 'Edit',
                                    class: 'text-center',
                                    render: value => (
                                        <a
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            href={value}
                                            className="btn btn-xs btn-secondary"
                                        >
                                            Edit
                                        </a>
                                    ),
                                },
                                {
                                    name: 'reviewLink',
                                    label: 'Review',
                                    class: 'text-center',
                                    render: value => (
                                        <a
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            href={value}
                                            className="btn btn-xs btn-secondary"
                                        >
                                            Review
                                        </a>
                                    ),
                                },
                            ]}
                        />
                        {closeButton}
                    </>
                );
            }}
        />
    );
};

export default SessionChecklist;
